import { BehaviorSubject, fromEvent, Observable, startWith, Subject, Subscription } from 'rxjs';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../../external/providers/textbook-actions-handler';
import { OperatingSystem } from "../../../../external/models/device-info.enum";

export enum WindowOrientation {
  Landscape,
  Portrait,
}

@Injectable()
export class UtilitiesService implements OnDestroy {
  private currentClickedTarget$ubject = new Subject<HTMLElement | null>();
  private windowOrientation$ubject = new BehaviorSubject<WindowOrientation | null>(null);
  private windowBounds$ubject = new BehaviorSubject<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  private currentDeviceOS!: string | null;
  private subscription = new Subscription();

  get windowBounds$(): Observable<{ width: number; height: number }> {
    return this.windowBounds$ubject.asObservable();
  }

  get currentClickedTarget$(): Observable<HTMLElement | null> {
    return this.currentClickedTarget$ubject.asObservable();
  }

  get windowOrientation$(): Observable<WindowOrientation | null> {
    return this.windowOrientation$ubject.asObservable();
  }

  get currentDeviceOs(): string | null {
    return this.currentDeviceOS;
  }

  get isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  get isVirtualKeyboardSupported(): boolean | null {
    const osWithVKeyboardNotSupported: string[] = [OperatingSystem.Mac, OperatingSystem.Ios];
    return this.currentDeviceOS ? !osWithVKeyboardNotSupported.includes(this.currentDeviceOS) : null;
  }

  constructor(
    private ngZone: NgZone,
    @Inject(TextbookActionsHandlerToken) public textbookActionsHandler: TextbookActionsHandler
  ) {
    ngZone.runOutsideAngular(() => {
      document.addEventListener('mousedown', this.onDocumentClick.bind(this), { passive: true });
      document.addEventListener('touchstart', this.onDocumentClick.bind(this), { passive: true });
      this.subscription.add(fromEvent(window, 'resize').subscribe(() => this.onWindowResize()));
      this.subscription.add(
        this.textbookActionsHandler.currentDeviceOS$.subscribe(
          (operatingSystem) => (this.currentDeviceOS = operatingSystem)
        )
      );
    });
  }

  private onDocumentClick(e: MouseEvent | TouchEvent): void {
    this.currentClickedTarget$ubject.next(e.target as HTMLElement);
  }

  private onOrientationChange(entries: ResizeObserverEntry[]): void {
    const width = entries ? entries[0]?.target?.clientWidth : window.innerWidth;
    const height = entries ? entries[0].target.clientHeight : window.innerHeight;
    this.windowOrientation$ubject.next(
      width > height ? WindowOrientation.Landscape : WindowOrientation.Portrait
    );
  }

  private onWindowResize(): void {
    const { innerWidth: width, innerHeight: height } = window;
    this.windowBounds$ubject.next({ width, height });
  }

  clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(value, max));

  percentage = (partialValue: number, totalValue: number): number =>
    (partialValue * 100) / totalValue;

  getCaretIndex(element: HTMLInputElement): number {
    let position = 0;
    const isSupported = typeof window.getSelection !== 'undefined';
    if (isSupported) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount !== 0) {
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        position = preCaretRange.toString().length;
      }
    }

    return position;
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.onDocumentClick);
    document.removeEventListener('touchstart', this.onDocumentClick);
    this.subscription.unsubscribe();
  }
}
